/**
 * `/new` — create a session, then get out of the way.
 *
 * "A session is created with a single tap" (`ProductOverview.md` §4.1), so this screen exists
 * only for as long as the request takes, and replaces itself in history so Back returns to the
 * landing page rather than creating a second session.
 *
 * A room bigger than two, or one that lasts longer than an hour, is chosen on `/group` first and
 * arrives here as query parameters. The default path stays exactly one tap.
 */

import { SESSION_LENGTHS, clampCapacity, type SessionLengthId } from '@beeline/shared';

import { api, ApiError } from '../lib/api';
import { el } from '../lib/dom';
import { rememberMembership } from '../lib/membership';
import { navigate } from '../lib/navigate';
import type { View } from '../lib/router';
import { withSimParams } from '../sim/peer';
import { errorScreen, spinner } from '../ui/feedback';

/** Read the room shape off the URL, falling back to the one-tap defaults. */
function requestedShape(): { capacity: number; length: SessionLengthId } {
  const params = new URLSearchParams(window.location.search);

  const capacity = params.has('capacity') ? clampCapacity(params.get('capacity')) : 2;
  const asked = params.get('length');
  const length = SESSION_LENGTHS.find((option) => option.id === asked)?.id ?? 'hour';

  return { capacity, length };
}

export const createView: View = (container) => {
  let cancelled = false;

  const showPending = (): void => {
    container.replaceChildren(
      el(
        'main',
        { class: 'screen screen--centered' },
        spinner(),
        el('p', { class: 'screen__body', text: 'Setting up a session…' }),
      ),
    );
  };

  const start = async (): Promise<void> => {
    showPending();

    try {
      const session = await api.createSession(requestedShape());
      if (cancelled) return;

      rememberMembership({
        code: session.code,
        participant: session.participant,
        token: session.token,
        name: null,
        expiresAt: session.expiresAt,
        created: true,
      });

      // replace, not push: Back should leave, not make another session.
      navigate(withSimParams(`/s/${session.code}`), { replace: true });
    } catch (error) {
      if (cancelled) return;

      const message =
        error instanceof ApiError && error.code === 'rate_limited'
          ? 'That is a lot of sessions at once. Give it a moment and try again.'
          : 'Could not start a session. Check your connection and try again.';

      container.replaceChildren(
        errorScreen({
          title: 'No session yet',
          body: message,
          actionLabel: 'Try again',
          onAction: () => void start(),
          secondaryLabel: 'Back',
          onSecondary: () => navigate('/'),
        }),
      );
    }
  };

  void start();

  return () => {
    cancelled = true;
  };
};
